import { memo, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useAdminAnalytics, PeriodPreset } from '../../hooks/useAdminAnalytics';
import DangerActionModal from '../../components/DangerActionModal';
import { useAdminPolicy } from '../../hooks/useAdminPolicy';
import { runAdminFloriaSync } from '../../api/admin.api';

function ComparisonBadge({ value }: { value: number }) {
  if (value === 0) return <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>0%</span>;
  const isUp = value > 0;
  const className = isUp ? 'admin-metric-up' : 'admin-metric-down';
  return (
    <span className={className} style={{ fontSize: '12px', marginLeft: '6px' }}>
      {isUp ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
}

const MetricCard = memo(function MetricCard({
  label,
  value,
  comparison,
  format = (v: number) => String(v),
  tooltip,
}: {
  label: string;
  value: number;
  comparison?: number | null;
  format?: (v: number) => string;
  tooltip?: string;
}) {
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showPopover) return;
    const onDocClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setShowPopover(false);
    };
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [showPopover]);

  return (
    <div className="admin-card admin-metric-card" ref={popoverRef}>
      <div className="admin-metric-label-row">
        <span>{label}</span>
        {tooltip && (
          <button
            type="button"
            className="admin-info-btn"
            onClick={(e) => { e.stopPropagation(); setShowPopover((v) => !v); }}
            aria-label="Подсказка"
          >
            ℹ
          </button>
        )}
      </div>
      {showPopover && tooltip && (
        <div className="admin-metric-popover" role="tooltip">
          {tooltip}
        </div>
      )}
      <div className="admin-metric-value">
        {format(value)}
        {comparison != null && <ComparisonBadge value={comparison} />}
      </div>
    </div>
  );
});

const CHART_EXPORT_COLORS = {
  accent: '#c2185b',
  grid: '#e0e0e0',
  text: '#666666',
  bg: '#ffffff',
};

function downloadChartAsPng(containerEl: HTMLElement, filename: string) {
  const svg = containerEl.querySelector('svg');
  if (!svg) return;
  const svgClone = svg.cloneNode(true) as SVGSVGElement;
  const rect = svg.getBoundingClientRect();
  const scale = 2;
  const origW = rect.width;
  const origH = Math.max(rect.height, 100);
  const w = Math.round(origW * scale);
  const h = Math.round(origH * scale);
  if (!svgClone.getAttribute('viewBox')) svgClone.setAttribute('viewBox', `0 0 ${origW} ${origH}`);
  svgClone.setAttribute('width', String(w));
  svgClone.setAttribute('height', String(h));
  svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  const svgStr = new XMLSerializer().serializeToString(svgClone)
    .replace(/var\(--color-accent\)/g, CHART_EXPORT_COLORS.accent)
    .replace(/var\(--border-soft\)/g, CHART_EXPORT_COLORS.grid)
    .replace(/var\(--text-tertiary\)/g, CHART_EXPORT_COLORS.text)
    .replace(/var\(--text-secondary\)/g, CHART_EXPORT_COLORS.text)
    .replace(/var\(--bg-surface\)/g, CHART_EXPORT_COLORS.bg);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = CHART_EXPORT_COLORS.bg;
  ctx.fillRect(0, 0, w, h);
  const img = new Image();
  img.onload = () => {
    ctx.drawImage(img, 0, 0, w, h);
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);
}

/** Контейнер графика с горизонтальным скроллом — только у графиков, метрики без скролла */
const CHART_SCROLL_STYLE: React.CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  overflowX: 'auto' as const,
  WebkitOverflowScrolling: 'touch',
};

/** Линейный график: сетка по Y, подписи значений, тултип при наведении (значение и разница с предыдущим днём). */
function LineChart({
  data,
  max,
  height = 100,
  formatLabel,
  formatValue = (v: number) => String(v),
  valueKey = 'value',
}: {
  data: { date: string; [k: string]: string | number }[];
  max: number;
  height?: number;
  formatLabel: (dateStr: string) => string;
  formatValue?: (v: number) => string;
  valueKey?: string;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && data.length > 0) el.scrollLeft = el.scrollWidth;
  }, [data.length]);

  if (!data.length) return null;
  const labelHeight = 20;
  const padding = { top: 12, right: 12, bottom: 12 + labelHeight, left: 48 };
  const minPxPerPoint = 28;
  const w = Math.max(280, data.length * minPxPerPoint);
  const svgHeight = height + labelHeight;
  const innerH = height - padding.top - (padding.bottom - labelHeight);
  const yScale = max > 0 ? (v: number) => padding.top + innerH - (v / max) * innerH : () => padding.top + innerH / 2;
  const xStep = data.length > 1 ? (w - padding.left - padding.right) / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const v = Number(d[valueKey]) || 0;
    const prevV = i > 0 ? Number(data[i - 1][valueKey]) || 0 : null;
    const delta = prevV !== null ? v - prevV : null;
    return { x: padding.left + i * xStep, y: yScale(v), date: d.date, value: v, delta };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const stroke = 'var(--color-accent)';
  const gridColor = 'var(--border-soft)';
  const yTicks = max > 0 ? [0, max / 2, max] : [0];

  const chartId = `chart-${data[0]?.date ?? 'x'}-${data.length}`;
  const stepLabels = data.length > 20 ? Math.ceil(data.length / 15) : 1;
  const labelsY = height + 14;
  const labelsToShow = data.filter((_, i) => i % stepLabels === 0 || i === data.length - 1);

  return (
    <div ref={scrollRef} className="admin-chart-scroll" style={CHART_SCROLL_STYLE}>
      <div style={{ minWidth: w, position: 'relative' }} data-chart-id={chartId}>
        <svg width={w} height={svgHeight} style={{ display: 'block' }} data-chart-export="true">
          {/* Горизонтальная сетка и подписи по Y */}
          {yTicks.map((tick, i) => {
            const y = yScale(tick);
            return (
              <g key={i}>
                <line x1={padding.left} x2={w - padding.right} y1={y} y2={y} stroke={gridColor} strokeWidth="1" strokeDasharray="4 2" />
                <text x={padding.left - 6} y={y + 4} textAnchor="end" fontSize="11" fill="var(--text-tertiary)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatValue(tick)}
                </text>
              </g>
            );
          })}
          <path d={pathD} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {points.map((p, i) => (
            <circle
              key={p.date}
              cx={p.x}
              cy={p.y}
              r={hoveredIndex === i ? 6 : 4}
              fill={stroke}
              stroke="var(--bg-surface)"
              strokeWidth="1.5"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{ cursor: 'pointer' }}
            />
          ))}
          {/* Подписи дат по оси X — внутри SVG для экспорта */}
          {labelsToShow.map((d) => {
            const i = data.indexOf(d);
            const x = padding.left + (data.length > 1 ? (i / (data.length - 1)) * (w - padding.left - padding.right) : 0);
            return (
              <text key={d.date} x={x} y={labelsY} textAnchor="middle" fontSize="10" fill="var(--text-secondary)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatLabel(d.date)}
              </text>
            );
          })}
        </svg>
        {hoveredIndex !== null && points[hoveredIndex] && (
          <div
            style={{
              position: 'absolute',
              left: Math.min(points[hoveredIndex].x, w - 140),
              top: 0,
              transform: 'translateY(-8px)',
              padding: '8px 12px',
              borderRadius: '8px',
              background: 'var(--bg-surface)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
              fontSize: '12px',
              fontVariantNumeric: 'tabular-nums',
              zIndex: 10,
              pointerEvents: 'none',
              border: '1px solid var(--border-soft)',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '2px' }}>{formatLabel(points[hoveredIndex].date)}</div>
            <div style={{ color: 'var(--text-primary)' }}>{formatValue(points[hoveredIndex].value)}</div>
            {points[hoveredIndex].delta !== null && (
              <div
                style={{
                  fontSize: '11px',
                  color: points[hoveredIndex].delta! > 0 ? '#0d7d3d' : points[hoveredIndex].delta! < 0 ? '#c85050' : 'var(--text-tertiary)',
                }}
              >
                {points[hoveredIndex].delta! > 0
                  ? `на ${formatValue(points[hoveredIndex].delta!)} больше чем в предыдущий день`
                  : points[hoveredIndex].delta! < 0
                    ? `на ${formatValue(-points[hoveredIndex].delta!)} меньше чем в предыдущий день`
                    : 'без изменения'}
              </div>
            )}
            {points[hoveredIndex].delta === null && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>первый день</div>}
          </div>
        )}
      </div>
    </div>
  );
}

/** Заголовок блока с опциональной иконкой подсказки и доп. контентом справа */
function BlockTitle({ title, tooltip, extra }: { title: string; tooltip?: string; extra?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [open]);
  return (
    <div className="admin-block-title-wrap" ref={ref}>
      <span className="admin-card-title">{title}</span>
      {(extra || tooltip) && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {extra}
          {tooltip && (
            <button type="button" className="admin-info-btn admin-info-btn--sm" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }} aria-label="Подсказка">ℹ</button>
          )}
        </span>
      )}
      {open && tooltip && <div className="admin-metric-popover admin-metric-popover--block">{tooltip}</div>}
    </div>
  );
}

/** Приглушённые цвета для полос воронки и источников */
const MUTED_BAR_COLORS = [
  '#7c8fb5', /* серо-синий */
  '#8a9b7a', /* оливковый */
  '#a67d6b', /* терракотовый */
  '#7d8fa8', /* голубовато-серый */
  '#9b8a7c', /* бежево-коричневый */
  '#7a8e9e', /* серо-голубой */
];

/** Для одного дня: 12 интервалов по 2 часа (0,2,4,...,22) */
function fillChartBy2hRange<T extends { date: string }>(
  raw: T[],
  dateYmd: string,
  getValue: (d: T) => number,
  valueKey: keyof T
): { date: string; [k: string]: string | number }[] {
  const byBucket = new Map<string, T>();
  for (const d of raw) byBucket.set(d.date, d);
  const result: { date: string; [k: string]: string | number }[] = [];
  for (let h = 0; h < 24; h += 2) {
    const key = `${dateYmd}T${String(h).padStart(2, '0')}`;
    const row = byBucket.get(key);
    result.push({ date: key, [valueKey as string]: row ? getValue(row) : 0 });
  }
  return result;
}

/** Заполняет массив по дням от from до to включительно, подставляет 0 для отсутствующих дат */
function fillChartByDateRange<T extends { date: string }>(
  raw: T[],
  from: string,
  to: string,
  getValue: (d: T) => number,
  valueKey: keyof T
): { date: string; [k: string]: string | number }[] {
  if (!from || !to) return raw as { date: string; [k: string]: string | number }[];
  const byDate = new Map<string, T>();
  for (const d of raw) byDate.set(d.date.slice(0, 10), d);
  const result: { date: string; [k: string]: string | number }[] = [];
  const fromD = new Date(from);
  const toD = new Date(to);
  for (let d = new Date(fromD); d <= toD; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    const row = byDate.get(key);
    result.push({
      date: key,
      [valueKey as string]: row ? getValue(row) : 0,
    });
  }
  return result;
}

/** Подпись для оси X: ДД.ММ или ЧЧ:00 для 2h */
function formatChartDateLabel(dateStr: string, bucket?: '2h' | 'day'): string {
  if (bucket === '2h' && /T\d{2}$/.test(dateStr)) {
    const h = dateStr.slice(-2);
    return `${parseInt(h, 10)}:00`;
  }
  const dateOnly = typeof dateStr === 'string' ? dateStr.slice(0, 10) : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateStr.slice(0, 7) || '';
  const [, month, day] = dateOnly.split('-');
  return `${parseInt(day, 10)}.${parseInt(month, 10)}`;
}

const PERIOD_OPTIONS: { preset: PeriodPreset; label: string }[] = [
  { preset: 'today', label: 'Сегодня' },
  { preset: '7d', label: '7 дней' },
  { preset: '30d', label: '30 дней' },
  { preset: 'all', label: 'За всё время' },
  { preset: 'custom', label: 'Свой период' },
];

export default function AdminDashboard() {
  const { data, loading, error, preset, from, to, setPreset, setCustomFrom, setCustomTo, refetch } = useAdminAnalytics('30d');
  const policy = useAdminPolicy();
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const revenueChartRef = useRef<HTMLDivElement>(null);
  const usersChartRef = useRef<HTMLDivElement>(null);
  const avgCheckChartRef = useRef<HTMLDivElement>(null);

  const handlePreset = useCallback((p: PeriodPreset) => setPreset(p), [setPreset]);

  const m = data?.metrics;
  const charts = data?.charts;
  const comparison = data?.comparison;
  const marketing = data?.marketing;
  const funnel = marketing?.funnel ?? [];
  const usersBySource = marketing?.usersBySourcePercent ?? [];
  const repeatPercent = marketing?.repeatPurchasesPercent ?? 0;
  const clientsWithoutOrdersPercent = marketing?.clientsWithoutOrdersPercent ?? 0;

  const chartBucket = charts?.chartBucket ?? 'day';
  const isSingleDay = from === to;
  const effectiveFrom = data?.periodFrom ?? from;
  const effectiveTo = data?.periodTo ?? to;

  const usersGrowthSlice = useMemo(() => {
    const raw = charts?.usersGrowth ?? [];
    if (chartBucket === '2h' && isSingleDay && from) return fillChartBy2hRange(raw, from, (d) => d.count, 'count');
    return fillChartByDateRange(raw, effectiveFrom, effectiveTo, (d) => d.count, 'count');
  }, [charts?.usersGrowth, from, effectiveFrom, effectiveTo, chartBucket, isSingleDay]);

  const revenueByDaySlice = useMemo(() => {
    const raw = charts?.revenueByDay ?? [];
    if (chartBucket === '2h' && isSingleDay && from) return fillChartBy2hRange(raw, from, (d) => d.total, 'total');
    return fillChartByDateRange(raw, effectiveFrom, effectiveTo, (d) => d.total, 'total');
  }, [charts?.revenueByDay, from, effectiveFrom, effectiveTo, chartBucket, isSingleDay]);

  const avgCheckByDayFilled = useMemo(() => {
    const raw = charts?.avgCheckByDay ?? [];
    if (chartBucket === '2h' && isSingleDay && from) return fillChartBy2hRange(raw, from, (d) => d.avgCheck, 'avgCheck');
    return fillChartByDateRange(raw, effectiveFrom, effectiveTo, (d) => d.avgCheck, 'avgCheck');
  }, [charts?.avgCheckByDay, from, effectiveFrom, effectiveTo, chartBucket, isSingleDay]);
  const maxGrowth = useMemo(() => Math.max(1, ...usersGrowthSlice.map((d) => Number(d.count) || 0)), [usersGrowthSlice]);
  const maxRevenue = useMemo(() => Math.max(1, ...revenueByDaySlice.map((d) => Number(d.total) || 0)), [revenueByDaySlice]);
  const avgCheckByDayMax = useMemo(() => Math.max(1, ...avgCheckByDayFilled.map((x) => Number(x.avgCheck) || 0)), [avgCheckByDayFilled]);

  const revenuePercent = comparison?.revenuePercent ?? 0;
  const alerts: { id: string; severity: 'warning' | 'critical'; text: string }[] = [];
  if (revenuePercent < -20) alerts.push({ id: 'rev', severity: 'critical', text: `Выручка за период на ${Math.abs(revenuePercent).toFixed(0)}% ниже предыдущего периода` });
  if (clientsWithoutOrdersPercent > 60) alerts.push({ id: 'noord', severity: 'warning', text: `Большая доля пользователей без заказов: ${clientsWithoutOrdersPercent.toFixed(0)}%` });

  if (loading && !data) {
    return (
      <div className="admin-page">
        <div className="admin-skeleton-table" style={{ padding: '16px', marginBottom: '16px' }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="admin-skeleton-row">
              <div className="admin-skeleton-cell" style={{ width: '25%' }} />
              <div className="admin-skeleton-cell" style={{ width: '50%' }} />
              <div className="admin-skeleton-cell" style={{ width: '25%' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-error">{error}</div>
      </div>
    );
  }
  if (!m) {
    return (
      <div className="admin-page">
        <div style={{ color: 'var(--text-secondary)', padding: '16px' }}>Нет данных</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {alerts.length > 0 && (
        <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
          {alerts.map((a) => (
            <div
              key={a.id}
              role="alert"
              style={{
                padding: '12px 14px',
                borderRadius: '10px',
                fontSize: '13px',
                lineHeight: 1.4,
                backgroundColor: a.severity === 'critical' ? 'rgba(200, 80, 80, 0.12)' : 'rgba(200, 160, 0, 0.12)',
                border: `1px solid ${a.severity === 'critical' ? '#c85050' : '#c4a000'}`,
                color: 'var(--text-primary)',
              }}
            >
              {a.severity === 'critical' ? '⚠️ ' : '💡 '}{a.text}
            </div>
          ))}
        </div>
      )}

      <div className="admin-filters" style={{ marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
        <label style={{ fontSize: '14px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Период:
          <select
            value={preset}
            onChange={(e) => handlePreset(e.target.value as PeriodPreset)}
            className="admin-input"
            style={{ minWidth: '140px', padding: '8px 12px' }}
          >
            {PERIOD_OPTIONS.map(({ preset: p, label }) => (
              <option key={p} value={p}>{label}</option>
            ))}
          </select>
        </label>
        {preset === 'custom' && (
          <>
            <input type="date" aria-label="Дата начала" value={from} onChange={(e) => setCustomFrom(e.target.value)} className="admin-input admin-input-date" />
            <input type="date" aria-label="Дата окончания" value={to} onChange={(e) => setCustomTo(e.target.value)} className="admin-input admin-input-date" />
          </>
        )}
        <button type="button" onClick={refetch} className="admin-btn admin-btn-secondary" title="Обновить данные" aria-label="Обновить данные дашборда">
          🔄 Обновить
        </button>
        <button
          type="button"
          className="admin-btn admin-btn-secondary"
          disabled={!policy.canRunFloriaSync || syncLoading}
          onClick={() => setSyncConfirmOpen(true)}
          title={policy.canRunFloriaSync ? 'Ручной запуск синхронизации Floria' : 'Действие временно недоступно'}
        >
          {syncLoading ? 'Синхронизация...' : 'Синхронизировать Floria'}
        </button>
      </div>
      {!policy.canRunFloriaSync && (
        <div className="admin-card" style={{ marginBottom: 12 }}>
          Ручная синхронизация временно недоступна.
        </div>
      )}
      {syncNotice && <div className="admin-card" style={{ marginBottom: 12 }}>{syncNotice}</div>}

      <div className="admin-dashboard-grid">
        <div className="admin-row-kpi admin-dashboard-metrics">
          <MetricCard
            label="Выручка"
            value={Number(m.totalRevenue) || 0}
            comparison={comparison?.revenuePercent != null ? Number(comparison.revenuePercent) : null}
            format={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`}
            tooltip="Сумма оплаченных заказов за выбранный период"
          />
          <MetricCard
            label="Себестоимость"
            value={Number(m.cogs) || 0}
            format={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`}
            tooltip="Сумма себестоимости товаров в оплаченных заказах за период."
          />
          <MetricCard
            label="Валовая прибыль"
            value={Number(m.grossProfit) || 0}
            comparison={comparison?.grossProfitPercent != null ? Number(comparison.grossProfitPercent) : null}
            format={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`}
            tooltip="Выручка минус себестоимость товаров (без учёта расходов на рекламу и операционных издержек)."
          />
          <MetricCard
            label="Маржинальность"
            value={Number(m.grossMarginPercent) || 0}
            format={(v) => `${Number(v).toFixed(1)}%`}
            tooltip="Валовая прибыль / выручка * 100."
          />
          <MetricCard
            label="Оплаченных заказов"
            value={Number(m.orderCount) || 0}
            comparison={comparison?.orderCountPercent != null ? Number(comparison.orderCountPercent) : null}
            tooltip="Количество оплаченных заказов за период"
          />
          <MetricCard
            label="Средний чек (AOV)"
            value={Number(m.avgCheck) || 0}
            format={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`}
            tooltip="Выручка за период ÷ число оплаченных заказов"
          />
          <MetricCard
            label="Новые пользователи"
            value={Number(m.newUsersPeriod) || 0}
            comparison={comparison?.newUsersPercent != null ? Number(comparison.newUsersPercent) : null}
            tooltip="Зарегистрировались за выбранный период"
          />
          <MetricCard
            label="Активные пользователи"
            value={Number(m.activeClients) ?? 0}
            format={(v) => String(Math.round(Number(v)))}
            tooltip="Уникальные пользователи с хотя бы одним оплаченным заказом за период"
          />
          <MetricCard
            label="ARPU"
            value={Number(m.arpu) || 0}
            format={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`}
            tooltip="Выручка за период ÷ активные пользователи"
          />
        </div>

        {revenueByDaySlice.length > 0 && (
          <div className="admin-card admin-dashboard-card-full" style={{ gridColumn: '1 / -1' }}>
            <BlockTitle title="Выручка по дням, ₽" tooltip="Сумма оплаченных заказов по каждому дню. Данные за выбранный период." />
            <div className="admin-chart-area" ref={revenueChartRef}>
              <LineChart data={revenueByDaySlice} max={maxRevenue} height={100} formatLabel={(d) => formatChartDateLabel(d, chartBucket)} formatValue={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`} valueKey="total" />
            </div>
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-sm"
                onClick={() => revenueChartRef.current && downloadChartAsPng(revenueChartRef.current, 'vyruchka-po-dnyam.png')}
              >
                Скачать график
              </button>
            </div>
          </div>
        )}

        {funnel.length > 0 && (
          <div className="admin-card admin-dashboard-card-full" style={{ gridColumn: '1 / -1' }}>
            <BlockTitle title="Воронка" tooltip="Доля от всех пользователей бота. Показаны шаги: зарегистрировались → оформили заказ → оплатили → сделали повторный заказ." />
            <p className="admin-card-subtitle">Доля от всех пользователей бота</p>
            <div className="admin-funnel-steps">
              {funnel.map((step, idx) => (
                <div key={step.label} className="admin-funnel-row" title={`Уникальные пользователи: ${step.label}`}>
                  <span className="admin-funnel-label">{step.label}</span>
                  <div className="admin-funnel-bar-wrap">
                    <div
                      className="admin-funnel-bar"
                      style={{
                        width: `${step.percent}%`,
                        backgroundColor: MUTED_BAR_COLORS[idx % MUTED_BAR_COLORS.length],
                        minWidth: step.percent > 0 ? '24px' : '0',
                      }}
                    />
                  </div>
                  <span className="admin-funnel-count">{step.count} ({step.percent.toFixed(1)}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(usersBySource.length > 0 || (charts?.trafficSources?.length ?? 0) > 0) ? (
          <div className="admin-card admin-dashboard-card-full" style={{ gridColumn: '1 / -1' }}>
            <BlockTitle title="Источники трафика" tooltip="Источник берётся из первого заказа. Для РСЯ, ВК, QR-кодов используйте разные start-параметры в ссылке (например t.me/Bot?start=rsya)." />
            <div className="admin-sources-list">
              {usersBySource.length > 0
                ? usersBySource.map((s, idx) => (
                    <div key={s.source} className="admin-source-row">
                      <span className="admin-source-label">{s.source}</span>
                      <div className="admin-source-bar-wrap">
                        <div
                          className="admin-source-bar"
                          style={{
                            width: `${s.percent}%`,
                            backgroundColor: MUTED_BAR_COLORS[idx % MUTED_BAR_COLORS.length],
                            minWidth: s.percent > 0 ? '20px' : '0',
                          }}
                        />
                      </div>
                      <span className="admin-source-count">{s.count} ({s.percent.toFixed(1)}%)</span>
                    </div>
                  ))
                : (charts?.trafficSources ?? []).map((s, idx) => {
                    const total = (charts?.trafficSources ?? []).reduce((sum, x) => sum + x.count, 0);
                    const pct = total > 0 ? (s.count / total) * 100 : 0;
                    return (
                      <div key={s.source} className="admin-source-row">
                        <span className="admin-source-label">{s.source}</span>
                        <div className="admin-source-bar-wrap">
                          <div
                            className="admin-source-bar"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: MUTED_BAR_COLORS[idx % MUTED_BAR_COLORS.length],
                              minWidth: pct > 0 ? '20px' : '0',
                            }}
                          />
                        </div>
                        <span className="admin-source-count">{s.count} ({pct.toFixed(1)}%)</span>
                      </div>
                    );
                  })}
            </div>
          </div>
        ) : null}

        <div className="admin-card admin-dashboard-card-full" style={{ gridColumn: '1 / -1' }}>
          <BlockTitle title="Пользовательские метрики" tooltip="Показатели вовлечённости и конверсии: повторные покупки, retention, конверсии воронки." />
          <div className="admin-metrics-grid">
            <MetricCard
              label="Повторные клиенты, %"
              value={repeatPercent}
              format={(v) => `${Number(v).toFixed(1)}%`}
              tooltip="Доля пользователей с ≥2 оплаченными заказами среди тех, у кого ≥1 оплаченный заказ"
            />
            <MetricCard label="Retention 7 дн." value={Number(m.retention7Days) ?? 0} format={(v) => `${Number(v).toFixed(1)}%`} tooltip="% пользователей, сделавших второй заказ в течение 7 дней после первого" />
            <MetricCard label="Retention 30 дн." value={Number(m.retention30Days) ?? 0} format={(v) => `${Number(v).toFixed(1)}%`} tooltip="% пользователей, сделавших второй заказ в течение 30 дней после первого" />
            <MetricCard label="Конверсия в заказ" value={Number(m.conversionToOrder ?? m.conversion) ?? 0} format={(v) => `${Number(v).toFixed(1)}%`} tooltip="Оформили заказ ÷ все пользователи" />
            <MetricCard label="Конверсия в оплату" value={Number(m.conversionToPayment) ?? 0} format={(v) => `${Number(v).toFixed(1)}%`} tooltip="Оплатили ÷ оформили заказ" />
            <MetricCard label="Конверсия от входа до оплаты" value={Number(m.conversionToPaymentFromStart) ?? 0} format={(v) => `${Number(v).toFixed(1)}%`} tooltip="Оплатили ÷ все пользователи" />
          </div>
        </div>

        {usersGrowthSlice.length > 0 && (
          <div className="admin-card admin-dashboard-card-full" style={{ gridColumn: '1 / -1' }}>
            <BlockTitle title="Рост пользователей, шт." tooltip="Количество новых регистраций по каждому дню. Данные за выбранный период." />
            <div className="admin-chart-area" ref={usersChartRef}>
              <LineChart data={usersGrowthSlice} max={maxGrowth} height={100} formatLabel={(d) => formatChartDateLabel(d, chartBucket)} formatValue={(v) => String(Math.round(v))} valueKey="count" />
            </div>
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-sm"
                onClick={() => usersChartRef.current && downloadChartAsPng(usersChartRef.current, 'rost-polzovateley.png')}
              >
                Скачать график
              </button>
            </div>
          </div>
        )}
        {avgCheckByDayFilled.length > 0 ? (
          <div className="admin-card admin-dashboard-card-full" style={{ gridColumn: '1 / -1' }}>
            <BlockTitle title="Средний чек по дням" tooltip="Выручка дня ÷ число оплаченных заказов в этот день." />
            <div className="admin-chart-area" ref={avgCheckChartRef}>
              <LineChart data={avgCheckByDayFilled} max={avgCheckByDayMax} height={100} formatLabel={(d) => formatChartDateLabel(d, chartBucket)} formatValue={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`} valueKey="avgCheck" />
            </div>
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-sm"
                onClick={() => avgCheckChartRef.current && downloadChartAsPng(avgCheckChartRef.current, 'sredniy-chek.png')}
              >
                Скачать график
              </button>
            </div>
          </div>
        ) : null}
      </div>
      <DangerActionModal
        open={syncConfirmOpen}
        title="Подтверждение синхронизации"
        description="Будет запущена ресурсоемкая ручная синхронизация. Во время выполнения возможна повышенная нагрузка."
        entityLabel="Floria Sync"
        confirmationText="SYNC"
        requireReason
        loading={syncLoading}
        error={syncError}
        onClose={() => {
          if (!syncLoading) setSyncConfirmOpen(false);
        }}
        onConfirm={async ({ reason }) => {
          setSyncLoading(true);
          setSyncError(null);
          try {
            await runAdminFloriaSync(reason);
            setSyncConfirmOpen(false);
            setSyncNotice('Синхронизация запущена.');
          } catch (e) {
            setSyncError(e instanceof Error ? e.message : 'Не удалось запустить синхронизацию');
          } finally {
            setSyncLoading(false);
          }
        }}
      />
    </div>
  );
}
